import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AlertController, IonRefresher, IonRouterOutlet, ModalController, NavController } from '@ionic/angular';
import { Socket } from 'ngx-socket-io';
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { MatchService } from '../_core/services/match.service';
import { Match } from '../_shared/models/match.model';
import { CreateMatchComponent } from './create-match/create-match.component';
import { ToastService } from '../_core/services/toast.service';
import { images } from '../_shared/data/image';
import { TranslateService } from '@ngx-translate/core';

@Component({
    standalone: false,
    selector: 'app-matches',
    templateUrl: './matches.page.html',
    styleUrls: ['./matches.page.scss'],
})

export class MatchesPage implements OnInit, OnDestroy {
    private translateService = inject(TranslateService);

    matches: Match[] = [];
    isLoading = false;
    getMoreMatches = false;
    page = 0;
    roomsToJoin = [];
    private destroyed$: Subject<void> = new Subject();

    filters = {
        owner: false,
        tracker: false,
        viewer: false,
        inProgress: false
    };
    individuals_placeholder = this.translateService.instant('Track scores & advanced statistics of your singles/doubles matches and share it all with friends and family in real-time! \n\n Click on the plus button below to get started!');
    public pickleballIcon = images.pickleball;

    constructor(
        private navCtrl: NavController,
        private alertCtrl: AlertController,
        private modalCtrl: ModalController,
        private toast: ToastService,
        private matchService: MatchService,
        private socket: Socket,
        private route: ActivatedRoute,
        private routerOutlet: IonRouterOutlet
    ) {
        this.getMatchUpdate().subscribe((data: any) => {
            if (data) {
                const matchIndex = this.matches.findIndex(match => match._id === data.match._id);
                this.matches[matchIndex] = data.match;
            }
        });
    }

    ngOnInit() {
        this.subscriptions();
        this.isLoading = true;
        this.getMatches();
        this.handleNavParams();

        // Subscribe to language changes to update placeholder text
        this.translateService.onLangChange.pipe(
            takeUntil(this.destroyed$)
        ).subscribe(() => {
            this.individuals_placeholder = this.translateService.instant('Track scores & advanced statistics of your singles/doubles matches and share it all with friends and family in real-time! \n\n Click on the plus button below to get started!');
        });
    }

    ngOnDestroy() {
        if (this.roomsToJoin) {
            this.socket.emit('leave-room', this.roomsToJoin);
        }
        this.socket.removeListener('match-update');
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    private getMatchUpdate() {
        const observable = new Observable(observer => {
            this.socket.on('match-update', data => {
                observer.next(data);
            });
        });
        return observable;
    }

    doRefresh(refresher: IonRefresher) {
        this.page = 0;
        this.matches = [];
        if (this.roomsToJoin) {
            this.socket.emit('leave-room', this.roomsToJoin);
        }
        this.getMatches(refresher);
    }

    doInfinite(infiniteScroll) {
        this.page++;
        this.getMatches();
        setTimeout(() => {
            infiniteScroll.target.complete();
        }, 1500);
    }

    getMatches(refresher?) {
        const paginationConfig = {
            page: this.page,
            sort: -1,
            limit: 10,
            type: 'individual',
            filters: this.filters
        };

        this.matchService.getOwnersMatches(paginationConfig)
            .pipe(
                finalize(() => {
                    this.isLoading = false;
                    if (refresher) {
                        refresher.detail.complete();
                    }
                }),
                takeUntil(this.destroyed$)
            )

            .subscribe(
                res => {
                    res.data.forEach(match => {
                        this.matches.push(match);
                        this.roomsToJoin.push(match._id);
                    });
                    this.socket.emit('join-room', this.roomsToJoin);

                    if (res.data.length < 10) {
                        this.getMoreMatches = false;
                    } else {
                        this.getMoreMatches = true;
                    }
                },
                responseError => {
                    //console.log(responseError);
                }
            );
    }

    openDetails(matchId) {
        this.navCtrl.navigateForward(['live-scoring/' + matchId]);
    }

    async openCreateMatchModal(partner?, code?) {
        const modal = await this.modalCtrl.create({
            component: CreateMatchComponent,
            componentProps: {
                partner: partner,
                code: code
            },
            id: 'create-match-modal',
            presentingElement: this.routerOutlet.nativeEl
        });

        await modal.present();

        const { data } = await modal.onDidDismiss();

        if (data) {
            const match = data.match;
            this.matches.unshift(match);
        }
    }

    private async subscriptions() {
        this.matchService
            .subscribeUnfollow()
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                matchId => {
                    if (matchId) {
                        this.removeMatch(matchId);
                    }
                }
            );

        this.matchService
            .subscribeDelete()
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                match => {
                    this.removeMatch(match);
                }
            );

        this.matchService
            .subscribeEdit()
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                updatedMatch => {
                    this.matches.forEach((match, i) => {
                        if (match._id === updatedMatch._id) {
                            this.matches[i] = updatedMatch;
                        }
                    });
                }
            );

        this.matchService
            .subscribeMatchFilterConfig()
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                (filters => {
                    this.page = 0;
                    this.matches = [];
                    if (this.roomsToJoin) {
                        this.socket.emit('leave-room', this.roomsToJoin);
                    }
                    this.filters = filters;
                    this.getMatches();

                })
            );

        this.matchService
            .subscribeAddEventRequest()
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                type => {
                    if (type === 'individual') {
                        this.openCreateMatchModal();
                    }
                }
            );
    }

    private handleFollowMatch(matchId) {
        this.matchService
            .follow(matchId)
            .subscribe(
                res => {
                    this.toast.presentToast('You are following a new match');
                    this.matches.unshift(res.data);
                },
                err => {
                    //console.log(err));
                }
            );
    }

    private removeMatch(removedMatch: Match) {
        this.matches.forEach((match, i) => {
            if (match._id === removedMatch._id) {
                this.matches.splice(i, 1);
            }
        });
    }

    private handleNavParams() {
        const params = { ...this.route.snapshot.queryParams };

        if (params.create) {
            this.openCreateMatchModal(params.partner, params.code);
        }

    }
}
